/**
 * Tab Renderer Registry (DRR-165: FormSchema-driven detail tabs)
 *
 * Maps renderer keys from FormSchema config `views.detail_tabs[].renderer`
 * to React component factories. The EntityDetailPanel queries the FormSchema,
 * reads the `views` section, and uses this registry to render each tab.
 *
 * FERP Principles:
 * - Backend-driven: renderer keys come from FormSchema config (not hardcoded)
 * - DRY: ONE registry for all primitive types
 * - Extensible: subgraphs register their own renderers via registerTabRenderer()
 * - Domain-agnostic: renderer keys are structural, not industry-specific
 *
 * Usage:
 * ```tsx
 * import { getTabRenderer, registerTabRenderer } from '@ferp/workflow-forms';
 *
 * // Register a custom renderer (once, at app init)
 * registerTabRenderer('custom-viz', MyCustomViz);
 *
 * // Get renderer for a tab (returns null if not registered)
 * const Renderer = getTabRenderer('workflow-state-viz');
 * if (Renderer) return <Renderer entity={entity} />;
 * ```
 */

import type { ComponentType } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

/** Props passed to every tab renderer component */
export interface TabRendererProps {
  /** The entity being viewed (full GraphQL response object) */
  entity: Record<string, unknown>;
  /** Tenant ID for scoped queries */
  tenantId: string;
  /** Primitive type (e.g., 'instance', 'workflow', 'task') */
  primitiveType: string;
  /** Additional props from the config tab definition */
  tabProps?: Record<string, unknown>;
}

/** Tab definition from FormSchema config views.detail_tabs[] */
export interface DetailTabConfig {
  id: string;
  label: string;
  icon: string;
  renderer: string;
  order: number;
  props?: Record<string, unknown>;
}

// ── Registry ─────────────────────────────────────────────────────────────────

const registry = new Map<string, ComponentType<TabRendererProps>>();

/**
 * Register a tab renderer component for a given renderer key.
 * Call at app initialization (e.g., in App.tsx or a setup module).
 */
export function registerTabRenderer(
  rendererKey: string,
  component: ComponentType<TabRendererProps>,
): void {
  registry.set(rendererKey, component);
}

/**
 * Get the registered tab renderer for a given key.
 * Returns null if no renderer is registered (tab shows fallback content).
 */
export function getTabRenderer(
  rendererKey: string,
): ComponentType<TabRendererProps> | null {
  return registry.get(rendererKey) ?? null;
}

/**
 * Check if a renderer is registered for a given key.
 */
export function hasTabRenderer(rendererKey: string): boolean {
  return registry.has(rendererKey);
}

/**
 * Get all registered renderer keys (for debugging/admin views).
 */
export function getRegisteredRenderers(): string[] {
  return Array.from(registry.keys());
}
